import { Box, Chip, Tooltip } from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import type { TLoadingStatusPill } from '../WidgetCard';
import type { TRefreshIndicatorType } from '../shared/utils';
import { useTranslation } from 'react-i18next';

export type TWidgetLoadingChipProps = {
  isLiveUpdating: boolean;
  setIsLiveUpdating: (value: boolean) => void;
  currentLoadingChipStatus: TLoadingStatusPill;
  isRendered?: boolean;
  setCurrentLoadingChipStatus: (value: TLoadingStatusPill) => void;
  type: TRefreshIndicatorType;
  refresh: number; // seconds
  isLive: boolean;
};

const WidgetLoadingChip = ({
  isLiveUpdating,
  setIsLiveUpdating,
  currentLoadingChipStatus,
  isRendered = true,
  setCurrentLoadingChipStatus,
  type,
  refresh,
  isLive,
}: TWidgetLoadingChipProps) => {
  const { t } = useTranslation();
  const chipLabel = isLive && !isLiveUpdating ? 'OFF' : currentLoadingChipStatus;

  const getTooltipTitle = () => {
    if (type === 'full') {
      return isLiveUpdating
        ? t('blox.dashboards.loading_chip.live_on', {
            refresh: Math.floor(refresh / 1000),
          })
        : t('blox.dashboards.loading_chip.live_off', {
            refresh: Math.floor(refresh / 1000),
          });
    }

    // Round up to 15, 30, or 60 (hourly) minute increments
    let refreshInMinutes = Math.ceil(refresh / 60000);
    if (refreshInMinutes <= 15) {
      refreshInMinutes = 15;
    } else if (refreshInMinutes <= 30) {
      refreshInMinutes = 30;
    } else {
      refreshInMinutes = 60;
    }

    if (refreshInMinutes === 60) {
      return t('blox.dashboards.loading_chip.hour');
    }

    return t('blox.dashboards.loading_chip.refresh', {
      refresh: refreshInMinutes,
    });
  };

  // If there is no widget?.parameters?.time_range property then return null.
  if (!isRendered || type === 'none') return null;

  return (
    <Tooltip
      title={getTooltipTitle()}
      placement="top"
      arrow
      enterTouchDelay={0}
      disableInteractive
    >
      <Box
        data-testid="widget-loading-chip"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'start',
          cursor: 'pointer',
        }}
      >
        {/* Full: show the live/off text. */}
        {type === 'full' ? (
          <Chip
            data-testid="trending-live-toggle"
            icon={<CircleIcon color="error" />}
            label={chipLabel}
            size="small"
            variant="outlined"
            onClick={() => {
              if (chipLabel === 'OFF') {
                setCurrentLoadingChipStatus('live');
              }
              setIsLiveUpdating(!isLiveUpdating);
            }}
            sx={(theme) => ({
              textTransform: 'capitalize',
              '& .MuiChip-icon': {
                color: isLiveUpdating
                  ? theme.palette.error.main
                  : theme?.blox?.colors?.bluegray,
              },
            })}
          />
        ) : (
          <CircleIcon
            data-testid="trending-refresh-indicator"
            color="error"
            sx={{ fontSize: 15, p: 0 }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

export default WidgetLoadingChip;
